import { Injectable } from '@angular/core';
import { Editor } from '@tiptap/core';
import { FrontendPhrase } from '../_interfaces/frontend-result';

@Injectable({ providedIn: 'root' })
export class TextService {
  private _allCapsMode = false;

  get isAllCapsMode(): boolean {
    return this._allCapsMode;
  }

  disableAllCaps(): void {
    this._allCapsMode = false;
  }

  async applyCommand(editor: Editor | null, command: FrontendPhrase): Promise<void> {
    if (!editor) return;

    const insert = (text: string) => {
      editor.chain().focus().insertContent(text).run();
    };

    switch (command.name) {
      // New lines / paragraphs
      case 'new-line':
        editor.chain().focus().enter().run();
        break;
      case 'new-paragraph':
        editor.chain().focus().enter().enter().run();
        break;

      // Punctuation
      case 'comma': insert(', '); break;
      case 'semicolon': insert('; '); break;
      case 'colon': insert(': '); break;
      case 'full stop':
      case 'period':
      case 'dot':
      case 'point': insert('. '); break;
      case 'exclamation mark': insert('! '); break;
      case 'question mark': insert('? '); break;
      case 'underscore': insert('_'); break;
      case 'hyphen':
      case 'minus sign': insert('-'); break;
      case 'ellipsis': insert('…'); break;

      // Quotes
      case 'begin single quote':
      case 'open single quote':
        insert("'");
        break;
      case 'close single quote':
      case 'end single quote':
        this.trimWhitespaceBeforeCursor(editor);
        insert("' ");
        break;
      case 'open quote':
      case 'begin quote':
        insert('"');
        break;
      case 'close quote':
      case 'end quote':
        this.trimWhitespaceBeforeCursor(editor);
        insert('" ');
        break;

      // Parentheses / Brackets
      case 'left parenthesis':
      case 'open parenthesis': insert('('); break;
      case 'right parenthesis':
      case 'close parenthesis':
        this.trimWhitespaceBeforeCursor(editor);
        insert(') ');
        break;
      case 'left bracket':
      case 'open bracket':
      case 'left square bracket':
      case 'open square bracket': insert('['); break;
      case 'right bracket':
      case 'close bracket':
      case 'right square bracket':
      case 'close square bracket':
        this.trimWhitespaceBeforeCursor(editor);
        insert('] ');
        break;
      case 'left brace':
      case 'open brace':
      case 'left curly bracket':
      case 'open curly bracket': insert('{'); break;
      case 'close brace':
      case 'right brace':
      case 'right curly bracket':
      case 'close curly bracket':
        this.trimWhitespaceBeforeCursor(editor);
        insert('} ');
        break;

      // Symbols
      case 'at sign':
      case 'at': insert('@'); break;
      case 'asterisk': insert('*'); break;
      case 'forward slash':
      case 'slash': insert('/'); break;
      case 'backslash': insert('\\'); break;
      case 'ampersand':
      case 'and sign': insert('&'); break;
      case 'number sign':
      case 'hash sign':
      case 'sharp sign': insert('#'); break;
      case 'percent sign': insert('%'); break;
      case 'plus sign':
      case 'plus': insert('+'); break;
      case 'less than sign':
      case 'left angle bracket':
      case 'open angle bracket': insert('<'); break;
      case 'equal sign': insert('='); break;
      case 'greater than sign':
      case 'right angle bracket':
      case 'close angle bracket': insert('>'); break;

      // Currency
      case 'dollar sign': insert('$'); break;
      case 'pound sterling sign':
      case 'pound sign': insert('£'); break;
      case 'euro sign': insert('€'); break;
      case 'yen sign': insert('¥'); break;

      // Misc symbols
      case 'apostrophe ess': insert('’s'); break;
      case 'apostrophe': insert('’'); break;
      case 'section sign': insert('§'); break;
      case 'paragraph sign': insert('¶'); break;
      case 'backquote': insert('`'); break;
      case 'caret': insert('^'); break;
      case 'degree sign': insert('°'); break;
      case 'division sign': insert('÷'); break;
      case 'multiplication sign': insert('×'); break;
      case 'vertical bar': insert('|'); break;
      case 'tilde': insert('~'); break;
      case 'winky face': insert('😉'); break;
      case 'copyright sign': insert('©'); break;
      case 'registered sign': insert('®'); break;

      // Clipboard & history
      case 'copy-that':
        await this.copySelectionToClipboard(editor);
        break;
      case 'cut-that':
        await this.copySelectionToClipboard(editor);
        this.deleteSelectionIfAny(editor);
        break;
      case 'paste-that':
        try {
          const text = await navigator.clipboard.readText();
          if (text) insert(text);
        } catch (e) {
          console.warn('Paste not permitted', e);
        }
        break;
      case 'undo-that':
      case 'undo-command':
        editor.chain().focus().undo().run();
        break;
      case 'redo-that':
        editor.chain().focus().redo().run();
        break;
      case 'delete-that':
        this.deleteSelectionIfAny(editor);
        break;

      // Deletions backward / within
      case 'delete-previous-word':
        this.deletePreviousWord(editor);
        break;
      case 'delete-previous-sentence':
        this.deletePreviousSentence(editor);
        break;
      case 'delete-previous-paragraph':
        this.deletePreviousParagraph(editor);
        break;
      case 'delete-sentence':
        this.deleteCurrentSentence(editor);
        break;
      case 'delete-paragraph':
        this.deleteCurrentParagraph(editor);
        break;

      // Selection helpers
      case 'unselect-text':
        this.clearSelectTextMode(editor);
        break;
      case 'select-paragraph':
        this.selectParagraphAtCursor(editor);
        break;
      case 'select-sentence':
        this.selectSentenceAtCursor(editor);
        break;
      case 'select-all':
        this.selectAll(editor);
        break;

      // Text transforms on selection
      case 'underline-that':
        editor.chain().focus().toggleUnderline().run();
        break;
      case 'uppercase-that':
        this.transformSelection(editor, (t) => t.toUpperCase());
        break;
      case 'lowercase-that':
        this.transformSelection(editor, (t) => t.toLowerCase());
        break;
      case 'italicise-that':
        editor.chain().focus().toggleItalic().run();
        break;
      case 'capitalise-that':
        this.transformSelection(editor, (t) => t.replace(/\b(\p{L})(\p{L}*)/gu, (_: string, a: string, b: string) => a.toUpperCase() + b.toLowerCase()));
        break;
      case 'bold-that':
        editor.chain().focus().toggleBold().run();
        break;

      // Navigation - previous
      case 'go-to-previous-sentence':
        this.moveToPreviousSentence(editor);
        break;
      case 'go-to-previous-paragraph':
        this.moveToPreviousParagraph(editor);
        break;
      case 'go-to-previous-line':
        this.moveToPreviousLine(editor);
        break;
      case 'go-to-previous-field':
        this.selectPreviousField(editor);
        break;

      // Navigation - next
      case 'go-to-next-sentence':
        this.moveToNextSentence(editor);
        break;
      case 'go-to-next-paragraph':
        this.moveToNextParagraph(editor);
        break;
      case 'go-to-next-line':
        this.moveToNextLine(editor);
        break;
      case 'go-to-next-field':
        this.selectNextField(editor);
        break;

      // Navigation - ends
      case 'go-to-end-of-sentence':
        this.moveToEndOfSentence(editor);
        break;
      case 'go-to-end-of-paragraph':
        this.moveToEndOfParagraph(editor);
        break;
      case 'go-to-end-of-document':
        this.moveToEndOfDocument(editor);
        break;
      case 'go-to-beginning-of-sentence':
        this.moveToBeginningOfSentence(editor);
        break;
      case 'go-to-beginning-of-paragraph':
        this.moveToBeginningOfParagraph(editor);
        break;
      case 'go-to-beginning-of-document':
        this.moveToBeginningOfDocument(editor);
        break;

      // Search/match patterns
      case 'previous-match':
        editor.commands.previousSearchResult();
        this.selectCurrentSearchResult(editor);
        break;
      case '[next-match]':
        editor.commands.nextSearchResult();
        this.selectCurrentSearchResult(editor);
        break;
      case 'select-<text>':
        this.handleSelectText(editor, command);
        break;
      case '[choose-<number>]':
        this.handleChooseNumber(editor, command);
        break;
      case '[select-<text>-through-<text>]':
        this.handleSelectTextThroughText(editor, command);
        break;
      case '[accept-all-defaults]':
        this.acceptAllDefaults(editor);
        break;
      case 'accept-defaults':
        this.acceptDefault(editor);
        break;
      case 'all-caps-on':
        this._allCapsMode = true;
        break;
      case 'all-caps-off':
        this._allCapsMode = false;
        break;

      default:
        insert(String(command.name) + ' ');
        break;
    }
  }

  private handleSelectText(editor: Editor, command: FrontendPhrase): void {
    const termRaw = (Array.isArray(command.parameters) && command.parameters.length > 0)
      ? String(command.parameters[0] ?? '')
      : '';
    const term = termRaw.trim();
    this.addSequentialPrefixesToMatches(editor, term);
    // Use SearchAndReplace extension to set search term and highlight
    editor.commands.setSearchTerm(term);
    editor.commands.resetIndex();
    this.selectCurrentSearchResult(editor);
  }

  clearSelectTextMode(editor: Editor, term?: string): void {
    this.removeSequentialPrefixesFromMatches(editor, term);
    editor.commands.setSearchTerm('');
    editor.commands.resetIndex();
    this.clearSelection(editor);
  }

  private addSequentialPrefixesToMatches(editor: Editor, term: string): void {
    if (!term) return;

    const flattened = this.buildFlattenedTextIndex(editor);
    const text = flattened.text;
    const positions = flattened.positions;
    if (!text || positions.length === 0) return;

    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedTerm, 'giu');
    const occurrences: Array<{ position: number; number: number }> = [];
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      if (!match[0].trim()) continue;
      const matchStart = match.index;
      const matchPosition = positions[matchStart];
      if (matchPosition === undefined) continue;

      const textBefore = text.slice(Math.max(0, matchStart - 20), matchStart);
      if (/\[\d+\]$/u.test(textBefore)) continue;

      occurrences.push({ position: matchPosition, number: occurrences.length + 1 });
    }

    if (occurrences.length === 0) return;

    const tr = editor.state.tr;
    for (let i = occurrences.length - 1; i >= 0; i -= 1) {
      const occurrence = occurrences[i];
      tr.insertText(`[${occurrence.number}]`, occurrence.position, occurrence.position);
    }
    editor.view.dispatch(tr);
  }

  private removeSequentialPrefixesFromMatches(editor: Editor, term?: string): void {
    const rawTerm = (term ?? String((editor as any).storage?.searchAndReplace?.searchTerm ?? '')).trim();
    if (!rawTerm) return;

    const flattened = this.buildFlattenedTextIndex(editor);
    const text = flattened.text;
    const positions = flattened.positions;
    if (!text || positions.length === 0) return;

    const escapedTerm = rawTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\[\\d+\\](?=${escapedTerm})`, 'giu');
    const ranges: Array<{ from: number; to: number }> = [];
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      const startIndex = match.index;
      const endIndexExclusive = startIndex + match[0].length;
      const from = positions[startIndex];
      const toAnchor = positions[endIndexExclusive - 1];
      if (from === undefined || toAnchor === undefined) continue;
      ranges.push({ from, to: toAnchor + 1 });
    }

    if (ranges.length === 0) return;

    const tr = editor.state.tr;
    for (let i = ranges.length - 1; i >= 0; i -= 1) {
      const range = ranges[i];
      tr.delete(range.from, range.to);
    }
    editor.view.dispatch(tr);
  }

  private handleChooseNumber(editor: Editor, command: FrontendPhrase): void {
    const raw = (Array.isArray(command.parameters) && command.parameters.length > 0) ? command.parameters[0] : 1;
    const n = Math.max(1, Number(raw) || 1);
    // choose is 1-based; delegate to plugin command
    editor.commands.chooseSearchResult(n);
    this.selectCurrentSearchResult(editor);
  }

  private handleSelectTextThroughText(editor: Editor, command: FrontendPhrase): void {
    const { startText, endText } = this.extractSelectThroughTerms(command);
    if (!startText || !endText) return;

    const flattened = this.buildFlattenedTextIndex(editor);
    const haystack = flattened.text;
    const positions = flattened.positions;
    if (!haystack || positions.length === 0) return;

    const haystackLower = haystack.toLowerCase();
    const startLower = startText.toLowerCase();
    const endLower = endText.toLowerCase();

    const startIndex = haystackLower.indexOf(startLower);
    if (startIndex < 0) return;

    const endSearchFrom = startIndex + startLower.length;
    const endIndex = haystackLower.indexOf(endLower, endSearchFrom);
    if (endIndex < 0) return;

    const from = positions[startIndex];
    const endLastCharIndex = endIndex + endLower.length - 1;
    const toAnchor = positions[endLastCharIndex];
    if (from === undefined || toAnchor === undefined) return;

    editor.chain().focus().setTextSelection({ from, to: toAnchor + 1 }).run();
  }

  private extractSelectThroughTerms(command: FrontendPhrase): { startText: string; endText: string } {
    const params = Array.isArray(command.parameters) ? command.parameters : [];
    if (params.length >= 2) {
      return {
        startText: String(params[0] ?? '').trim(),
        endText: String(params[1] ?? '').trim()
      };
    }

    const raw = String(params[0] ?? '').trim();
    if (!raw) return { startText: '', endText: '' };

    const throughToken = /\s+-?through-?\s+/iu;
    const pieces = raw.split(throughToken);
    if (pieces.length >= 2) {
      return { startText: pieces[0].trim(), endText: pieces[1].trim() };
    }

    return { startText: raw, endText: '' };
  }

  private acceptDefault(editor: Editor): void {
    const field = this.findSelectedOrContainingField(editor);
    if (!field) return;

    const fieldText = editor.state.doc.textBetween(field.from, field.to, '\n');
    if (!fieldText.startsWith('[') || !fieldText.endsWith(']')) return;

    const unwrapped = fieldText.slice(1, -1);
    const to = field.from + unwrapped.length;
    editor.chain().focus().insertContentAt({ from: field.from, to: field.to }, unwrapped).setTextSelection({ from: to, to }).run();
  }

  private acceptAllDefaults(editor: Editor): void {
    const matches = this.findFieldMatches(editor);
    if (matches.length === 0) return;

    const tr = editor.state.tr;
    for (let i = matches.length - 1; i >= 0; i -= 1) {
      const field = matches[i];
      const fieldText = editor.state.doc.textBetween(field.from, field.to, '\n');
      if (!fieldText.startsWith('[') || !fieldText.endsWith(']')) continue;
      tr.insertText(fieldText.slice(1, -1), field.from, field.to);
    }

    editor.view.dispatch(tr);
    editor.commands.focus();
  }

  private findSelectedOrContainingField(editor: Editor): { from: number; to: number } | null {
    const matches = this.findFieldMatches(editor);
    if (matches.length === 0) return null;

    const { from, to } = editor.state.selection;
    const selected = matches.find((field) => field.from === from && field.to === to);
    if (selected) return selected;

    const containing = matches.find((field) => from >= field.from && to <= field.to);
    if (containing) return containing;

    const overlapping = matches.find((field) => from < field.to && to > field.from);
    if (overlapping) return overlapping;

    return null;
  }

  private selectCurrentSearchResult(editor: Editor): void {
    const storage = (editor as any).storage?.searchAndReplace;
    if (!storage) return;
    const { results, resultIndex } = storage;
    const match = Array.isArray(results) ? results[resultIndex] : undefined;
    if (match) {
      const selectionFrom = this.findNumberPrefixStart(editor, match.from);
      editor.chain().focus().setTextSelection({ from: selectionFrom, to: match.to }).run();
    }
  }

  private findNumberPrefixStart(editor: Editor, from: number): number {
    const scanStart = Math.max(0, from - 16);
    const textBeforeMatch = editor.state.doc.textBetween(scanStart, from, '\n');
    const prefixMatch = textBeforeMatch.match(/\[\d+\]$/u);
    if (!prefixMatch) return from;
    return from - prefixMatch[0].length;
  }

  private deleteSelectionIfAny(editor: Editor): void {
    const { from, to } = editor.state.selection;
    if (from !== to) {
      editor.chain().focus().deleteSelection().run();
    }
  }

  private async copySelectionToClipboard(editor: Editor): Promise<void> {
    const { from, to } = editor.state.selection;
    if (from === to) return;
    const text = editor.state.doc.textBetween(from, to, '\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      console.warn('Copy to clipboard failed', e);
    }
  }

  private clearSelection(editor: Editor): void {
    const { to } = editor.state.selection;
    editor.chain().focus().setTextSelection({ from: to, to }).run();
  }

  private trimWhitespaceBeforeCursor(editor: Editor): void {
    const { from, to } = editor.state.selection;
    if (from !== to) return;

    const scanStart = Math.max(0, from - 200);
    const textBeforeCursor = editor.state.doc.textBetween(scanStart, from, '\n');
    const trailingWhitespace = textBeforeCursor.match(/\s+$/u);
    if (!trailingWhitespace) return;

    const trailingWhitespaceLength = trailingWhitespace[0].length;
    editor.chain().focus().deleteRange({ from: from - trailingWhitespaceLength, to: from }).run();
  }

  private selectAll(editor: Editor): void {
    const size = editor.state.doc.content.size;
    editor.chain().focus().setTextSelection({ from: 0, to: size }).run();
  }

  private transformSelection(editor: Editor, transform: (text: string) => string): void {
    const { from, to } = editor.state.selection;
    if (from === to) return;
    const original = editor.state.doc.textBetween(from, to, '\n');
    const updated = transform(original);
    editor.chain().focus().insertContentAt({ from, to }, updated).run();
  }

  // === Paragraph helpers ===
  private findParagraphBoundsAtCursor(editor: Editor): { from: number; to: number } | null {
    const { $from } = editor.state.selection as any;
    for (let depth = $from.depth; depth >= 0; depth--) {
      const node = $from.node(depth);
      if (node && node.type && node.type.name === 'paragraph') {
        return { from: $from.start(depth), to: $from.end(depth) };
      }
    }
    return null;
  }

  private selectParagraphAtCursor(editor: Editor): void {
    const bounds = this.findParagraphBoundsAtCursor(editor);
    if (!bounds) return;
    editor.chain().focus().setTextSelection(bounds).run();
  }

  private deleteCurrentParagraph(editor: Editor): void {
    const bounds = this.findParagraphBoundsAtCursor(editor);
    if (!bounds) return;
    editor.chain().focus().deleteRange(bounds).run();
  }

  private deletePreviousParagraph(editor: Editor): void {
    const { $from } = editor.state.selection as any;
    for (let depth = $from.depth; depth >= 0; depth--) {
      const node = $from.node(depth);
      if (node && node.type && node.type.name === 'paragraph') {
        const prevPos = $from.before(depth);
        if (prevPos > 0) {
          const tempPos = Math.max(1, prevPos - 1);
          const tr = editor.view.state.tr.setSelection((editor.view.state as any).selection.constructor.near(editor.view.state.doc.resolve(tempPos)));
          editor.view.dispatch(tr);
          const bounds = this.findParagraphBoundsAtCursor(editor);
          if (bounds) {
            editor.chain().focus().deleteRange(bounds).run();
          }
        }
        break;
      }
    }
  }

  private moveToPreviousParagraph(editor: Editor): void {
    const { $from } = editor.state.selection as any;
    for (let depth = $from.depth; depth >= 0; depth--) {
      const beforePos = $from.before(depth);
      if (beforePos > 0) {
        const probe = Math.max(1, beforePos - 1);
        const tr = editor.view.state.tr.setSelection((editor.view.state as any).selection.constructor.near(editor.view.state.doc.resolve(probe)));
        editor.view.dispatch(tr);
        const bounds = this.findParagraphBoundsAtCursor(editor);
        if (bounds) {
          editor.chain().focus().setTextSelection({ from: bounds.to, to: bounds.to }).run();
        }
        break;
      }
    }
  }

  private moveToNextParagraph(editor: Editor): void {
    const { $to } = editor.state.selection as any;
    for (let depth = $to.depth; depth >= 0; depth--) {
      const afterPos = $to.after(depth);
      if (afterPos < editor.state.doc.content.size) {
        const probe = Math.min(editor.state.doc.content.size - 1, afterPos);
        const tr = editor.view.state.tr.setSelection((editor.view.state as any).selection.constructor.near(editor.view.state.doc.resolve(probe)));
        editor.view.dispatch(tr);
        const bounds = this.findParagraphBoundsAtCursor(editor);
        if (bounds) {
          editor.chain().focus().setTextSelection({ from: bounds.to, to: bounds.to }).run();
        }
        break;
      }
    }
  }

  private moveToBeginningOfParagraph(editor: Editor): void {
    const bounds = this.findParagraphBoundsAtCursor(editor);
    if (!bounds) return;
    editor.chain().focus().setTextSelection({ from: bounds.from, to: bounds.from }).run();
  }

  private moveToEndOfParagraph(editor: Editor): void {
    const bounds = this.findParagraphBoundsAtCursor(editor);
    if (!bounds) return;
    editor.chain().focus().setTextSelection({ from: bounds.to, to: bounds.to }).run();
  }

  // === Sentence helpers (heuristic using punctuation) ===
  private getCursorPos(editor: Editor): number {
    return editor.state.selection.from;
  }

  private findSentenceBoundsAt(editor: Editor, pos: number): { from: number; to: number } {
    // Boundaries: last newline or '.' before caret, and next newline or '.' after caret, within the current paragraph
    const para = this.findParagraphBoundsAtCursor(editor);
    if (!para) return { from: 0, to: 0 };

    const slice = this.buildFlattenedTextIndexInRange(editor, para.from, para.to);
    const text = slice.text;
    const positions = slice.positions;
    const hardBreakBoundaries = slice.hardBreakBoundaries;

    if (text.length === 0) return { from: para.from, to: para.from };

    let caretCharIndex = -1;
    for (let i = 0; i < positions.length; i++) {
      if (positions[i] < pos) caretCharIndex = i; else break;
    }

    const lastDotIdx = text.lastIndexOf('.', Math.max(0, caretCharIndex));
    let lastBreakPosIdx = -1;
    for (let i = 0; i < hardBreakBoundaries.length; i++) {
      const boundaryIndex = hardBreakBoundaries[i];
      if (boundaryIndex - 1 <= caretCharIndex) lastBreakPosIdx = boundaryIndex - 1; else break;
    }
    const prevBoundaryPosIdx = Math.max(lastDotIdx, lastBreakPosIdx);
    const fromCharIdx = Math.max(0, prevBoundaryPosIdx + 1);

    let nextDotIdx = -1;
    for (let i = Math.max(0, caretCharIndex + 1); i < text.length; i++) {
      if (text[i] === '.') { nextDotIdx = i; break; }
    }
    let nextBreakPosIdx = Number.POSITIVE_INFINITY;
    for (let i = 0; i < hardBreakBoundaries.length; i++) {
      const boundaryIndex = hardBreakBoundaries[i];
      if (boundaryIndex - 1 > caretCharIndex) { nextBreakPosIdx = boundaryIndex - 1; break; }
    }
    let toCharIdx = text.length - 1;
    if (nextDotIdx >= 0 && nextDotIdx <= nextBreakPosIdx) toCharIdx = nextDotIdx; else if (Number.isFinite(nextBreakPosIdx)) toCharIdx = nextBreakPosIdx;

    const fromDoc = positions[Math.min(fromCharIdx, positions.length - 1)] ?? para.from;
    const toDoc = (positions[Math.min(toCharIdx, positions.length - 1)] ?? (para.to - 1)) + 1;
    return { from: fromDoc, to: toDoc };
  }

  private selectSentenceAtCursor(editor: Editor): void {
    const pos = this.getCursorPos(editor);
    const bounds = this.findSentenceBoundsAt(editor, pos);
    editor.chain().focus().setTextSelection(bounds).run();
  }

  private deleteCurrentSentence(editor: Editor): void {
    const pos = this.getCursorPos(editor);
    const bounds = this.findSentenceBoundsAt(editor, pos);
    editor.chain().focus().deleteRange(bounds).run();
  }

  private deletePreviousSentence(editor: Editor): void {
    const pos = this.getCursorPos(editor);
    const para = this.findParagraphBoundsAtCursor(editor);
    if (!para) return;
    const slice = this.buildFlattenedTextIndexInRange(editor, para.from, para.to);
    const text = slice.text;
    const positions = slice.positions;
    const hardBreakBoundaries = slice.hardBreakBoundaries;

    let caretCharIndex = -1;
    for (let i = 0; i < positions.length; i++) {
      if (positions[i] < pos) caretCharIndex = i; else break;
    }
    const lastDotIdx = text.lastIndexOf('.', Math.max(0, caretCharIndex));
    let lastBreakPosIdx = -1;
    for (let i = 0; i < hardBreakBoundaries.length; i++) {
      const boundaryIndex = hardBreakBoundaries[i];
      if (boundaryIndex - 1 <= caretCharIndex) lastBreakPosIdx = boundaryIndex - 1; else break;
    }
    const prevBoundaryPosIdx = Math.max(lastDotIdx, lastBreakPosIdx);
    const fromCharIdx = Math.max(0, prevBoundaryPosIdx + 1);
    const fromDoc = positions[Math.min(fromCharIdx, positions.length - 1)] ?? para.from;
    editor.chain().focus().deleteRange({ from: fromDoc, to: pos }).run();
  }

  private moveToPreviousSentence(editor: Editor): void {
    const pos = this.getCursorPos(editor);
    const para = this.findParagraphBoundsAtCursor(editor);
    if (!para) return;
    const slice = this.buildFlattenedTextIndexInRange(editor, para.from, para.to);
    const text = slice.text;
    const positions = slice.positions;
    const hardBreakBoundaries = slice.hardBreakBoundaries;

    let caretCharIndex = -1;
    for (let i = 0; i < positions.length; i++) {
      if (positions[i] < pos) caretCharIndex = i; else break;
    }
    const lastDotIdx = text.lastIndexOf('.', Math.max(0, caretCharIndex));
    let lastBreakPosIdx = -1;
    for (let i = 0; i < hardBreakBoundaries.length; i++) {
      const boundaryIndex = hardBreakBoundaries[i];
      if (boundaryIndex - 1 <= caretCharIndex) lastBreakPosIdx = boundaryIndex - 1; else break;
    }
    const prevBoundaryPosIdx = Math.max(lastDotIdx, lastBreakPosIdx);
    if (prevBoundaryPosIdx >= 0) {
      const newPos = positions[Math.min(prevBoundaryPosIdx, positions.length - 1)] + 1;
      editor.chain().focus().setTextSelection({ from: newPos, to: newPos }).run();
      return;
    }
    const { $from } = editor.state.selection as any;
    for (let depth = $from.depth; depth >= 0; depth--) {
      const beforePos = $from.before(depth);
      if (beforePos > 0) {
        const probe = Math.max(1, beforePos - 1);
        const tr = editor.view.state.tr.setSelection((editor.view.state as any).selection.constructor.near(editor.view.state.doc.resolve(probe)));
        editor.view.dispatch(tr);
        const prevBounds = this.findParagraphBoundsAtCursor(editor);
        if (prevBounds) {
          editor.chain().focus().setTextSelection({ from: prevBounds.to, to: prevBounds.to }).run();
        }
        break;
      }
    }
  }

  private moveToNextSentence(editor: Editor): void {
    const pos = this.getCursorPos(editor);
    const para = this.findParagraphBoundsAtCursor(editor);
    if (!para) return;
    const slice = this.buildFlattenedTextIndexInRange(editor, para.from, para.to);
    const text = slice.text;
    const positions = slice.positions;
    const hardBreakBoundaries = slice.hardBreakBoundaries;

    let caretCharIndex = -1;
    for (let i = 0; i < positions.length; i++) {
      if (positions[i] < pos) caretCharIndex = i; else break;
    }

    let firstBoundaryPosIdx = Number.POSITIVE_INFINITY;
    for (let i = Math.max(0, caretCharIndex + 1); i < text.length; i++) {
      if (text[i] === '.') { firstBoundaryPosIdx = i; break; }
    }
    for (let i = 0; i < hardBreakBoundaries.length; i++) {
      const bp = hardBreakBoundaries[i] - 1;
      if (bp > caretCharIndex && bp < firstBoundaryPosIdx) { firstBoundaryPosIdx = bp; break; }
    }

    if (Number.isFinite(firstBoundaryPosIdx)) {
      const newPos = positions[Math.min(firstBoundaryPosIdx as number, positions.length - 1)] + 1;
      editor.chain().focus().setTextSelection({ from: newPos, to: newPos }).run();
      return;
    }

    const { $to } = editor.state.selection as any;
    for (let depth = $to.depth; depth >= 0; depth--) {
      const afterPos = $to.after(depth);
      if (afterPos < editor.state.doc.content.size) {
        const probe = Math.min(editor.state.doc.content.size - 1, afterPos);
        const tr = editor.view.state.tr.setSelection((editor.view.state as any).selection.constructor.near(editor.view.state.doc.resolve(probe)));
        editor.view.dispatch(tr);
        const nextBounds = this.findParagraphBoundsAtCursor(editor);
        if (nextBounds) {
          editor.chain().focus().setTextSelection({ from: nextBounds.to, to: nextBounds.to }).run();
        }
        break;
      }
    }
  }

  private moveToBeginningOfSentence(editor: Editor): void {
    const pos = this.getCursorPos(editor);
    const para = this.findParagraphBoundsAtCursor(editor);
    if (!para) return;
    const slice = this.buildFlattenedTextIndexInRange(editor, para.from, para.to);
    const text = slice.text;
    const positions = slice.positions;
    const hardBreakBoundaries = slice.hardBreakBoundaries;

    let caretCharIndex = -1;
    for (let i = 0; i < positions.length; i++) {
      if (positions[i] < pos) caretCharIndex = i; else break;
    }
    const lastDotIdx = text.lastIndexOf('.', Math.max(0, caretCharIndex));
    let lastBreakPosIdx = -1;
    for (let i = 0; i < hardBreakBoundaries.length; i++) {
      const boundaryIndex = hardBreakBoundaries[i];
      if (boundaryIndex - 1 <= caretCharIndex) lastBreakPosIdx = boundaryIndex - 1; else break;
    }
    const prevBoundaryPosIdx = Math.max(lastDotIdx, lastBreakPosIdx);
    const newPos = positions[Math.max(0, Math.min(prevBoundaryPosIdx + 1, positions.length - 1))] ?? para.from;
    editor.chain().focus().setTextSelection({ from: newPos, to: newPos }).run();
  }

  private moveToEndOfSentence(editor: Editor): void {
    const pos = this.getCursorPos(editor);
    const para = this.findParagraphBoundsAtCursor(editor);
    if (!para) return;
    const slice = this.buildFlattenedTextIndexInRange(editor, para.from, para.to);
    const text = slice.text;
    const positions = slice.positions;
    const hardBreakBoundaries = slice.hardBreakBoundaries;

    let caretCharIndex = -1;
    for (let i = 0; i < positions.length; i++) {
      if (positions[i] < pos) caretCharIndex = i; else break;
    }
    let nextBoundaryPosIdx = Number.POSITIVE_INFINITY;
    for (let i = Math.max(0, caretCharIndex + 1); i < text.length; i++) {
      if (text[i] === '.') { nextBoundaryPosIdx = i; break; }
    }
    for (let i = 0; i < hardBreakBoundaries.length; i++) {
      const bp = hardBreakBoundaries[i] - 1;
      if (bp > caretCharIndex && bp < nextBoundaryPosIdx) { nextBoundaryPosIdx = bp; break; }
    }
    const boundaryIdx = Number.isFinite(nextBoundaryPosIdx) ? Math.min(nextBoundaryPosIdx as number, positions.length - 1) : positions.length - 1;
    const boundaryPos = positions[boundaryIdx] ?? (para.to - 1);
    editor.chain().focus().setTextSelection({ from: boundaryPos + 1, to: boundaryPos + 1 }).run();
  }

  // === Word helpers ===
  private deletePreviousWord(editor: Editor): void {
    const pos = this.getCursorPos(editor);
    const doc = editor.state.doc;
    const windowSize = 200;
    const start = Math.max(0, pos - windowSize);
    const before = doc.textBetween(start, pos, '\n');

    if (!before) return;

    const trailingSpaces = before.match(/\s+$/u);
    if (trailingSpaces) {
      const len = trailingSpaces[0].length;
      editor.chain().focus().deleteRange({ from: pos - len, to: pos }).run();
      return;
    }

    const lastWord = before.match(/[\p{L}\p{N}_]+$/u);
    if (lastWord) {
      const len = lastWord[0].length;
      editor.chain().focus().deleteRange({ from: pos - len, to: pos }).run();
      return;
    }

    editor.chain().focus().deleteRange({ from: Math.max(0, pos - 1), to: pos }).run();
  }

  // === Line helpers (heuristic using hard breaks / block boundaries) ===
  private moveToPreviousLine(editor: Editor): void {
    const pos = this.getCursorPos(editor);
    const doc = editor.state.doc;
    const textBefore = doc.textBetween(0, pos, '\n');
    const lastBreak = textBefore.lastIndexOf('\n');
    const newPos = lastBreak >= 0 ? lastBreak : 0;
    editor.chain().focus().setTextSelection({ from: newPos, to: newPos }).run();
  }

  private moveToNextLine(editor: Editor): void {
    const pos = this.getCursorPos(editor);
    const doc = editor.state.doc;
    const size = doc.content.size;
    const textAfter = doc.textBetween(pos, size, '\n');
    const nextBreak = textAfter.indexOf('\n');
    const newPos = nextBreak >= 0 ? pos + nextBreak + 1 : size;
    editor.chain().focus().setTextSelection({ from: newPos, to: newPos }).run();
  }

  // === Document edges ===
  private moveToBeginningOfDocument(editor: Editor): void {
    editor.chain().focus().setTextSelection({ from: 0, to: 0 }).run();
  }

  private moveToEndOfDocument(editor: Editor): void {
    const size = editor.state.doc.content.size;
    editor.chain().focus().setTextSelection({ from: size, to: size }).run();
  }

  // === Match navigation (based on current selection text) ===
  private selectNextMatch(editor: Editor): void {
    const { from, to } = editor.state.selection;
    if (from === to) return;
    const query = editor.state.doc.textBetween(from, to, '\n');
    const doc = editor.state.doc;
    const size = doc.content.size;
    const afterText = doc.textBetween(to, size, '\n');
    const idx = afterText.indexOf(query);
    if (idx >= 0) {
      const start = to + idx;
      const end = start + query.length;
      editor.chain().focus().setTextSelection({ from: start, to: end }).run();
    }
  }

  private selectPreviousMatch(editor: Editor): void {
    const { from, to } = editor.state.selection;
    if (from === to) return;
    const query = editor.state.doc.textBetween(from, to, '\n');
    const doc = editor.state.doc;
    const beforeText = doc.textBetween(0, from, '\n');
    const idx = beforeText.lastIndexOf(query);
    if (idx >= 0) {
      const start = idx;
      const end = start + query.length;
      editor.chain().focus().setTextSelection({ from: start, to: end }).run();
    }
  }

  // === Field placeholder navigation: selects bracketed groups like [ ... ] ===
  private selectNextField(editor: Editor): void {
    const matches = this.findFieldMatches(editor);
    if (matches.length === 0) return;
    const { to } = editor.state.selection;
    const next = matches.find(m => m.from > to);
    if (next) {
      editor.chain().focus().setTextSelection({ from: next.from, to: next.to }).run();
    }
  }

  private selectPreviousField(editor: Editor): void {
    const matches = this.findFieldMatches(editor);
    if (matches.length === 0) return;
    const { from } = editor.state.selection;
    let prev: { from: number; to: number } | undefined;
    for (const m of matches) {
      if (m.to < from) prev = m; else break;
    }
    if (prev) {
      editor.chain().focus().setTextSelection({ from: prev.from, to: prev.to }).run();
    }
  }

  private findFieldMatches(editor: Editor): { from: number; to: number }[] {
    const doc = editor.state.doc as any;
    const flattened = this.buildFlattenedTextIndex(editor);
    const text = flattened.text;
    const positions = flattened.positions;

    const matches: { from: number; to: number }[] = [];
    const pattern = /\[[^\]]*?\]/g;
    for (let m = pattern.exec(text); m; m = pattern.exec(text)) {
      const matchStartIndex = m.index;
      const matchEndIndexExclusive = matchStartIndex + m[0].length;
      if (matchStartIndex < positions.length && matchEndIndexExclusive - 1 < positions.length) {
        const fromPos = positions[matchStartIndex];
        const toPos = positions[matchEndIndexExclusive - 1] + 1; // end is exclusive in selections
        matches.push({ from: fromPos, to: toPos });
      }
    }
    return matches;
  }

  private buildFlattenedTextIndex(editor: Editor): { text: string; positions: number[] } {
    const doc = editor.state.doc as any;
    let text = '';
    const positions: number[] = [];
    doc.descendants((node: any, pos: number) => {
      if (node.isText && typeof node.text === 'string' && node.text.length > 0) {
        for (let i = 0; i < node.text.length; i++) {
          text += node.text[i];
          positions.push(pos + i);
        }
      }
      return true;
    });
    return { text, positions };
  }

  private buildFlattenedTextIndexInRange(editor: Editor, from: number, to: number): { text: string; positions: number[]; hardBreakBoundaries: number[] } {
    const doc = editor.state.doc as any;
    let text = '';
    const positions: number[] = [];
    const hardBreakBoundaries: number[] = [];

    doc.nodesBetween(from, to, (node: any, pos: number) => {
      if (node.type && node.type.name === 'hardBreak') {
        hardBreakBoundaries.push(text.length);
        return false;
      }
      if (node.isText && typeof node.text === 'string' && node.text.length > 0) {
        const start = Math.max(0, from - pos);
        const end = Math.min(node.text.length, to - pos);
        for (let i = start; i < end; i++) {
          text += node.text[i];
          positions.push(pos + i);
        }
      }
      return true;
    });
    const uniq = Array.from(new Set(hardBreakBoundaries)).sort((a, b) => a - b);
    return { text, positions, hardBreakBoundaries: uniq };
  }
}


